import { useCallback, useEffect, useMemo, useState } from 'react';
import { Space, Steps, Spin } from 'antd';
import styled from '@emotion/styled';
import { CheckCircleFilled, CloseCircleFilled } from '@ant-design/icons';
import { isUndefined } from 'lodash-es';
import useAuthShopUtils from 'hooks/business/authShops/useShopeeUtils';
import { useTranslate } from '@tolgee/react';
import { BottomButton, Footer } from './styles';
import { ShopeeAuthProgress } from '../hooks/useShopeeAuthShop';
import { useOrgValue } from 'hooks/business/system/useOrg';
import { BootGuideAuthTip, GotoBootGuideButton } from 'views/BootGuide/components/GuideLink';
import { isMasterOnline, isSiteGiant } from '../../../productLine';

const Content = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 660px;
  height: 210px;
  margin: 34px auto 40px;
  background: var(--fett-erp-card-bg);

  .ant-spin-text {
    color: var(--fett-text-primary);
  }
`;
const SmallSuccessIcon = styled(CheckCircleFilled)`
  font-size: 16px;
  color: var(--ant-success-color);
  margin-left: 2px;
`;
const SmallErrorIcon = styled(CloseCircleFilled)`
  font-size: 16px;
  color: var(--ant-error-color);
  margin-left: 2px;
`;
const ResultContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  .anticon {
    font-size: 60px;
    margin-bottom: 10px;
  }
  .anticon-check-circle {
    color: var(--ant-success-color);
  }
  .anticon-close-circle {
    color: var(--ant-error-color);
  }
`;
const ResultTitle = styled.div`
  font-size: 16px;
  font-weight: 500;
`;
const ResultDescription = styled.div`
  margin-top: 6px;
  font-size: 14px;
`;

export interface ShopeeAccountResultProps {
  progress?: ShopeeAuthProgress;
  loading: boolean;
  authResult?: RPA.Shopee.AuthAgentJSON;
  isConfigService?: boolean;
  onReStart: () => void;
  onFinish: (redirect?: string) => void;
  onConfigService?: (e: any) => void;
}

const ShopeeAccountResult = (props: ShopeeAccountResultProps) => {
  const { progress, loading, authResult, onReStart, onFinish } = props;
  const [current, setCurrent] = useState<number>(0);
  const { getAuthDescription } = useAuthShopUtils();
  const { t } = useTranslate();
  const orgValue = useOrgValue();
  const isExpired = orgValue?.isExpired;

  const steps = useMemo(() => {
    return [
      {
        title: t('authShop.登录卖家中心'),
        content: (
          <span>
            {t('authShop.正在登录卖家中心')}
            <br />
            {t('authShop.授权过程中部分帐号Shopee官方会发送验证码')}
          </span>
        ),
      },
      {
        title: t('authShop.买家消息数据授权'),
        content: (
          <span>
            {t('authShop.正在通过卖家中心授权')}
            <br />
            {t('authShop.买家消息数据')}
          </span>
        ),
      },
      {
        title: t('authShop.商品订单数据授权'),
        content: (
          <span>
            {t('authShop.正在通过卖家中心授权')}
            <br />
            {t('authShop.订单商品数据')}
          </span>
        ),
      },
    ];
  }, [t]);

  useEffect(() => {
    if (!isUndefined(progress) && progress.length) {
      setCurrent(progress.length - 1);
    }
  }, [progress]);

  const btnDisabled = useMemo(() => {
    return loading || !authResult || (authResult && authResult.status !== 'DoneWithFinishAll');
  }, [authResult, loading]);

  const renderStep = useCallback(
    (item, index) => {
      let description = null;
      if (Array.isArray(progress) && progress[index]) {
        const logs = progress[index];
        description = logs.map((log) => {
          let icon = null;
          if (log.type === 'success') {
            icon = <SmallSuccessIcon />;
          }
          if (log.type === 'error') {
            icon = <SmallErrorIcon />;
          }
          return (
            <div>
              {log.times} {log.message}
              {icon}
            </div>
          );
        });
      }

      return <Steps.Step key={item.title} title={item.title} description={description} />;
    },
    [progress],
  );

  const renderResult = useMemo(() => {
    if (authResult && authResult) {
      if (authResult?.status === 'DoneWithFinishAll') {
        return (
          <ResultContainer>
            <CheckCircleFilled />
            <ResultTitle>{t('authShop.已经成功添加店铺')}</ResultTitle>
            <ResultDescription>{t('authShop.添加更多店铺')}</ResultDescription>
          </ResultContainer>
        );
      }
      const reason = getAuthDescription(authResult);
      return (
        <ResultContainer>
          <CloseCircleFilled />
          <ResultTitle>{reason.title}</ResultTitle>
          <ResultDescription>{reason.subTitle}</ResultDescription>
        </ResultContainer>
      );
    }
    return null;
  }, [authResult, getAuthDescription, t]);

  return (
    <div>
      <Steps current={current}>{steps.map((item, index) => renderStep(item, index))}</Steps>
      <Content>
        {loading ? <Spin spinning tip={steps[current] && steps[current].content} /> : null}
        {!loading && authResult ? renderResult : null}
      </Content>
      {!btnDisabled && <BootGuideAuthTip className="mb-[26px]" />}
      <Footer>
        {authResult && authResult?.status !== 'DoneWithFinishAll' ? (
          <BottomButton size="large" type="primary" onClick={onReStart}>
            {t('authShop.重新添加店铺')}
          </BottomButton>
        ) : (
          <Space>
            {isExpired ? (
              isSiteGiant || isMasterOnline ? null : (
                <BottomButton
                  size="large"
                  type="primary"
                  disabled={btnDisabled}
                  onClick={() => onFinish('/management/service/market')}
                >
                  {t('shop_auth_result_goto_order_package')}
                </BottomButton>
              )
            ) : (
              <GotoBootGuideButton
                size="large"
                disabled={btnDisabled}
                onClick={() => onFinish()}
                fallback={
                  <BottomButton
                    size="large"
                    type="primary"
                    disabled={btnDisabled}
                    onClick={() => onFinish('/conversation')}
                  >
                    {t('authShop.立即开始接待')}
                  </BottomButton>
                }
              />
            )}
            <BottomButton size="large" disabled={btnDisabled} onClick={onReStart}>
              {t('authShop.再次添加新的店铺')}
            </BottomButton>
          </Space>
        )}
      </Footer>
    </div>
  );
};

export default ShopeeAccountResult;
